import { Routes } from '@angular/router';
import { activeOrganizationGuard } from './core/guards/active-organization.guard';
import { backOfficeGuard } from './core/guards/back-office.guard';
import { marketplaceAdminGuard } from './core/guards/marketplace-admin.guard';
import { eisGuard } from './core/guards/eis.guard';
import { staffAreaGuard } from './core/guards/staff-area.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { unverifiedUserGuard } from './core/guards/unverified-user.guard';
import { verifiedUserGuard } from './core/guards/verified-user.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./auth/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'public/kunjungan/:token',
    loadComponent: () =>
      import('./public/pages/public-kunjungan/public-kunjungan.page').then(
        (m) => m.PublicKunjunganPage,
      ),
  },
  {
    path: 'public/titip-barang/:token',
    loadComponent: () =>
      import('./public/pages/public-titip-barang/public-titip-barang.page').then(
        (m) => m.PublicTitipBarangPage,
      ),
  },
  {
    path: 'public/titip-obat/:token',
    loadComponent: () =>
      import('./public/pages/public-titip-obat/public-titip-obat.page').then(
        (m) => m.PublicTitipObatPage,
      ),
  },
  {
    path: 'register',
    loadComponent: () => import('./auth/register/register.page').then((m) => m.RegisterPage),
  },
  {
    path: 'home',
    loadComponent: () => import('./home/home.page').then((m) => m.HomePage),
  },
  {
    path: 'reservasi',
    loadChildren: () =>
      import('./reservasi/reservasi.routes').then((m) => m.RESERVASI_ROUTES),
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
  },
  {
    path: 'marketplace',
    loadChildren: () =>
      import('./marketplace/marketplace.routes').then((m) => m.MARKETPLACE_ROUTES),
    canActivate: [activeOrganizationGuard],
  },
  {
    path: 'titip-barang',
    loadChildren: () =>
      import('./titip-barang/titip-barang.routes').then((m) => m.TITIP_BARANG_ROUTES),
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
  },
  {
    path: 'pembebasan-bersyarat',
    loadChildren: () =>
      import('./pembebasan-sementara/pembebasan-sementara.routes').then(
        (m) => m.PEMBEBASAN_SEMENTARA_ROUTES,
      ),
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
  },
  {
    path: 'izin-luar-biasa',
    loadChildren: () =>
      import('./izin-luar-biasa/izin-luar-biasa.routes').then(
        (m) => m.IZIN_LUAR_BIASA_ROUTES,
      ),
    canActivate: [activeOrganizationGuard, verifiedUserGuard]
  },
  {
    path: 'kesehatan',
    loadChildren: () =>
      import('./kesehatan/kesehatan.routes').then((m) => m.KESEHATAN_ROUTES),
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
  },
  {
    path: 'jadwal-kunjungan',
    loadChildren: () =>
      import('./jadwal-kunjungan/jadwal-kunjungan.routes').then(
        (m) => m.JADWAL_KUNJUNGAN_ROUTES,
      ),
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
  },
  {
    path: 'layanan-pengaduan',
    canActivate: [activeOrganizationGuard, verifiedUserGuard],
    loadChildren: () =>
      import('./layanan-pengaduan/layanan-pengaduan.routes').then(
        (m) => m.LAYANAN_PENGADUAN_ROUTES,
      ),
  },
  {
    path: 'account',
    loadChildren: () =>
      import('./account/account.routes').then((m) => m.ACCOUNT_ROUTES),
    canActivate: [activeOrganizationGuard],
  },
  {
    path: 'admin',
    loadComponent: () => import('./admin/admin-shell.component').then((m) => m.AdminShellComponent),
    canActivate: [activeOrganizationGuard, backOfficeGuard],
    canActivateChild: [staffAreaGuard],
    children: [
      {
        path: 'master/tahanan',
        loadComponent: () =>
          import('./admin/master/tahanan/tahanan.page').then((m) => m.TahananPage),
      },
      {
        path: 'master/cooperative-products',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./admin/master/cooperative-products/cooperative-products.page').then(
            (m) => m.CooperativeProductsPage,
          ),
      },
      {
        path: 'master/handcraft-products',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./admin/master/handcraft-products/handcraft-products.page').then(
            (m) => m.HandcraftProductsPage,
          ),
      },
      {
        path: 'layanan/pembebasan-bersyarat',
        loadComponent: () =>
          import('./admin/pembebasan-sementara/list.page').then(
            (m) => m.AdminPembebasanSementaraListPage,
          ),
      },
      {
        path: 'layanan/pembebasan-bersyarat/:id',
        loadComponent: () =>
          import('./admin/pembebasan-sementara/detail.page').then(
            (m) => m.AdminPembebasanSementaraDetailPage,
          ),
      },
      {
        path: 'layanan/izin-luar-biasa',
        loadComponent: () =>
          import('./admin/izin-luar-biasa/list.page').then(
            (m) => m.AdminIzinLuarBiasaListPage,
          ),
      },
      {
        path: 'layanan/izin-luar-biasa/:id',
        loadComponent: () =>
          import('./admin/izin-luar-biasa/detail.page').then(
            (m) => m.AdminIzinLuarBiasaDetailPage,
          ),
      },
      {
        path: 'layanan/jadwal-kunjungan',
        loadChildren: () =>
          import('./jadwal-kunjungan/admin-jadwal-kunjungan.routes').then(
            (m) => m.ADMIN_JADWAL_KUNJUNGAN_ROUTES,
          ),
      },
      {
        path: 'pengaturan/profil-jaksa',
        loadComponent: () =>
          import('./users/pages/prosecutor-profile-settings/prosecutor-profile-settings.page').then(
            (m) => m.ProsecutorProfileSettingsPage,
          ),
      },
      {
        path: 'pengaturan/pengguna',
        pathMatch: 'full',
        redirectTo: 'pengaturan/pengguna-warga',
      },
      {
        path: 'pengaturan/pengguna-warga',
        loadComponent: () =>
          import('./admin/users/list.page').then((m) => m.AdminUsersListPage),
        data: { userType: 'public' },
      },
      {
        path: 'pengaturan/pengguna-internal',
        loadComponent: () =>
          import('./admin/users/list.page').then((m) => m.AdminUsersListPage),
        data: { userType: 'internal' },
      },
      {
        path: 'pengaturan/pembayaran',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-payment-config/admin-payment-config.page').then(
            (m) => m.AdminPaymentConfigPage,
          ),
      },
      {
        path: 'pengaturan/pengiriman',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-shipping-config/admin-shipping-config.page').then(
            (m) => m.AdminShippingConfigPage,
          ),
      },
      {
        path: 'pengaturan/kontak',
        loadComponent: () =>
          import('./admin/pengaturan/kontak/kontak.page').then(
            (m) => m.AdminKontakPage,
          ),
      },
      {
        path: 'pengaturan/organisasi',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./admin/organizations/list.page').then(
            (m) => m.AdminOrganizationsListPage,
          ),
      },
      {
        path: 'pengaturan/organisasi/buat',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./admin/organizations/form.page').then(
            (m) => m.AdminOrganizationFormPage,
          ),
      },
      {
        path: 'pengaturan/organisasi/:id/edit',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./admin/organizations/form.page').then(
            (m) => m.AdminOrganizationFormPage,
          ),
      },
      {
        path: 'pengaturan/jadwal-tatap-muka-titip-barang',
        loadComponent: () =>
          import('./jadwal-kunjungan/pages/admin/admin-visit-schedule/admin-visit-schedule.page').then(
            (m) => m.AdminVisitSchedulePage,
          ),
      },
      {
        path: 'pengaturan/pengguna/:id',
        loadComponent: () =>
          import('./admin/users/detail.page').then((m) => m.AdminUserDetailPage),
      },
      {
        path: 'layanan/titip-barang',
        loadComponent: () =>
          import('./titip-barang/pages/admin/admin-titip-list/admin-titip-list.page').then(
            (m) => m.AdminTitipListPage,
          ),
      },
      {
        path: 'layanan/titip-barang/:id',
        loadComponent: () =>
          import('./titip-barang/pages/admin/admin-titip-detail/admin-titip-detail.page').then(
            (m) => m.AdminTitipDetailPage,
          ),
      },
      {
        path: 'layanan/pengaduan',
        loadComponent: () =>
          import('./admin/layanan-pengaduan/list.page').then(
            (m) => m.AdminPengaduanListPage,
          ),
      },
      {
        path: 'layanan/pengaduan/:id',
        loadComponent: () =>
          import('./admin/layanan-pengaduan/detail.page').then(
            (m) => m.AdminPengaduanDetailPage,
          ),
      },
      {
        path: 'marketplace/orders',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-orders/admin-orders.page').then((m) => m.AdminOrdersPage),
      },
      {
        path: 'marketplace/orders/:orderId',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-order-detail/admin-order-detail.page').then(
            (m) => m.AdminOrderDetailPage,
          ),
      },
      {
        path: 'marketplace/tel-voucher',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-tel-voucher/admin-tel-voucher.page').then(
            (m) => m.AdminTelVoucherPage,
          ),
      },
      {
        path: 'marketplace/categories',
        canActivate: [marketplaceAdminGuard],
        loadComponent: () =>
          import('./marketplace/admin/admin-categories/admin-categories.page').then(
            (m) => m.AdminCategoriesPage,
          ),
      },
      {
        path: 'layanan/verification-requests',
        loadComponent: () =>
          import('./admin/verification-requests/list.page').then(
            (m) => m.AdminVerificationRequestsListPage,
          ),
      },
      {
        path: 'layanan/verification-requests/:id',
        loadComponent: () =>
          import('./admin/verification-requests/detail.page').then(
            (m) => m.AdminVerificationRequestsDetailPage,
          ),
      },
      {
        path: 'layanan/narapidana-additions',
        loadComponent: () =>
          import('./admin/narapidana-additions/list.page').then(
            (m) => m.AdminNarapidanaAdditionsListPage,
          ),
      },
      {
        path: 'kesehatan',
        loadComponent: () =>
          import('./admin/kesehatan/admin-kesehatan-landing.page').then(
            (m) => m.AdminKesehatanLandingPage,
          ),
      },
      {
        path: 'kesehatan/manajemen-obat',
        redirectTo: 'kesehatan?tab=manajemen-obat',
      },
      {
        path: 'kesehatan/titip-obat',
        loadComponent: () =>
          import('./admin/kesehatan/titip-obat/admin-titip-obat-list.page').then(
            (m) => m.AdminTitipObatListPage,
          ),
      },
      {
        path: 'kesehatan/titip-obat/:id',
        loadComponent: () =>
          import('./admin/kesehatan/titip-obat/admin-titip-obat-detail.page').then(
            (m) => m.AdminTitipObatDetailPage,
          ),
      },
      {
        path: 'kesehatan/rawat-rumah-sakit',
        loadComponent: () =>
          import('./admin/kesehatan/rawat-rumah-sakit/list/rawat-rs-list.page').then(
            (m) => m.RawatRsListPage,
          ),
      },
      {
        path: 'kesehatan/rawat-rumah-sakit/buat',
        loadComponent: () =>
          import('./admin/kesehatan/rawat-rumah-sakit/create/rawat-rs-create.page').then(
            (m) => m.RawatRsCreatePage,
          ),
      },
      {
        path: 'kesehatan/rawat-rumah-sakit/:id',
        loadComponent: () =>
          import('./admin/kesehatan/rawat-rumah-sakit/detail/rawat-rs-detail.page').then(
            (m) => m.RawatRsDetailPage,
          ),
      },
      {
        path: 'kesehatan/rekam-medis',
        loadComponent: () =>
          import('./admin/kesehatan/rekam-medis/list/rekam-medis-list.page').then(
            (m) => m.RekamMedisListPage,
          ),
      },
      {
        path: 'kesehatan/rekam-medis/:id',
        loadComponent: () =>
          import('./admin/kesehatan/rekam-medis/detail/rekam-medis-detail.page').then(
            (m) => m.RekamMedisDetailPage,
          ),
      },
      {
        path: 'dapur',
        loadComponent: () => import('./admin/dapur/list/dapur-list.page').then((m) => m.DapurListPage),
      },
      {
        path: 'dapur/buat',
        loadComponent: () => import('./admin/dapur/form/dapur-form.page').then((m) => m.DapurFormPage),
      },
      {
        path: 'dapur/:id',
        loadComponent: () =>
          import('./admin/dapur/detail/dapur-detail.page').then((m) => m.DapurDetailPage),
      },
      {
        path: 'dapur/:id/edit',
        loadComponent: () => import('./admin/dapur/form/dapur-form.page').then((m) => m.DapurFormPage),
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./admin/admin-default.page').then((m) => m.AdminDefaultPage),
      },
      {
        path: 'eis',
        loadChildren: () => import('./admin/eis/eis.routes').then((m) => m.EIS_ROUTES),
        canActivate: [eisGuard],
      },
    ],
  },
  {
    path: 'design-system',
    loadChildren: () =>
      import('./design-system/showcase/showcase.routes').then((m) => m.SHOWCASE_ROUTES),
  },
  { path: '', pathMatch: 'full', redirectTo: 'login' },
];
